import { useCallback } from 'react';
import { makeStyles } from '@ellucian/react-design-system/core';
import { spacing40, fontWeightBold } from '@ellucian/react-design-system/core/styles/tokens';
import { useHistory } from 'react-router-dom';
import useApplicationForm from './useApplicationForm';
import { routes, CONSTANTS } from '../Helpers/Configuration';
import useRoot from '../RootContext/useRoot';
import { useSelector } from 'react-redux';
import { isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
import { selectFilesArray } from '../../redux/shared/selectors/applicationSelectors';
import { Typography, ListItem, List, Button } from '@ellucian/react-design-system/core';
import { FormattedMessage } from 'react-intl';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import ConditionalLogicCheck from './ConditionalLogicCheck';
import ApplicationFormSideBarListItem from './ApplicationFormSideBarListItem';
const useStyles = makeStyles({
    name: 'ApplicationFormSideBar'
})(theme => ({
    action: {
        margin: spacing40,
        '& svg': {
            marginRight: spacing40,
        },
    },
    listItem: {
        cursor: 'pointer',
    },
    pageList: {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        maxWidth: '300px',
        [theme.breakpoints.down('lg')]: {
            maxWidth: '100%',
        },
    },
    title: {
        fontWeight: fontWeightBold,
        margin: spacing40,
    },
}));
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectApplicationIsArchived = state => state.application?.archivedOn;
const selectApplicationIsWithdrawn = state => state.application?.status === CONSTANTS.applicationFormStatuses.WITHDRAWN;
const selectApplicationIsIncomplete = state => state.application?.status === CONSTANTS.applicationFormStatuses.INCOMPLETE;
const selectApplicationIsPaymentPending = state => state.application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending;
const selectHasSupportingDocuments = state => selectFilesArray(state).length > 0;

const ApplicationFormSideBar = props => {
    const {
        compact = false,
        onPageClick: onPageClickProp,
        page = null,
    } = props;
    const {
        clearApplicationSession,
        externalMode,
        importAdminMode,
        previewMode,
        template = { pages: [] },
    } = useApplicationForm();
    const rootCtx = useRoot();
    const { classes } = useStyles();
    const history = useHistory();
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const isArchived = useSelector(selectApplicationIsArchived);
    const isWithdrawn = useSelector(selectApplicationIsWithdrawn);
    const isIncomplete = useSelector(selectApplicationIsIncomplete);
    const isPaymentPending = useSelector(selectApplicationIsPaymentPending);
    const hasSupportingDocuments = useSelector(selectHasSupportingDocuments);
    const goToApplicationsHome = useCallback(() => {
        clearApplicationSession();
        rootCtx.clearTheme();
        history.push(routes.home);
    }, [clearApplicationSession, history]);
    const onPageClick = useCallback(value => {
        onPageClickProp && onPageClickProp(value);
    }, [onPageClickProp]);
    const goToSupplementalChecklist = () => {
        onPageClick(CONSTANTS.pageNames.supplementalItems);
    };
    const goToApplicationSubmitted = () => {
        onPageClick(CONSTANTS.pageNames.submission);
    };
    const goToSupportingDocuments = () => {
        onPageClick(CONSTANTS.pageNames.supportingDocuments);
    };
    const goToReview = () => {
        onPageClick(CONSTANTS.pageNames.review);
    };
    return (
        <div>
            {!compact &&
                <div className={classes.action}>
                    {!externalMode && !importAdminMode &&
                        <Button disabled={previewMode} variant="text" onClick={goToApplicationsHome}>
                            <Icon name="home" />
                            <FormattedMessage id="applicationsHome" />
                        </Button>
                    }
                </div>
            }
            {!compact && (!isSubmitted && !isArchived && !isWithdrawn && !isIncomplete) &&
                <Typography className={classes.title} component="div">
                    <FormattedMessage id="admissionsRequirements" />
                </Typography>
            }
            <List classes={{ root: classes.pageList }} role="menu">
                {((!isSubmitted && !isPaymentPending) && !isArchived && !isWithdrawn && !isIncomplete) && template.pages.map(it => {
                    return (
                        <ConditionalLogicCheck key={it.id}
                            conditions={it.conditions}
                            content={it}
                            contentType={CONSTANTS.appTemplateControlTypes.page}
                        >
                            <ApplicationFormSideBarListItem
                                active={it.id === page?.id}
                                onClick={onPageClick}
                                page={it}
                            />
                        </ConditionalLogicCheck>
                    );
                })}
                {(!isSubmitted && !isArchived && !isWithdrawn && !importAdminMode && !isIncomplete) &&
                    <ListItem divider onClick={goToReview} className={classes.listItem} role="menuitem">
                        <Typography>
                            <FormattedMessage id="reviewAndSubmit" />
                        </Typography>
                    </ListItem>
                }
                {(isSubmitted || isArchived || isWithdrawn || isIncomplete) &&
                    <>
                        <ListItem divider onClick={goToApplicationSubmitted} className={classes.listItem} role="menuitem">
                            <Typography>
                                <FormattedMessage id="applicationView" />
                            </Typography>
                        </ListItem>
                        {hasSupportingDocuments &&
                            <ListItem divider onClick={goToSupportingDocuments} className={classes.listItem} role="menuitem">
                                <Typography>
                                    <FormattedMessage id="supportingDocuments" />
                                </Typography>
                            </ListItem>
                        }
                        {template?.settings?.enableSupplementalItems && !!template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0 &&
                            <ListItem divider onClick={goToSupplementalChecklist} className={classes.listItem} role="menuitem">
                                <Typography>
                                    <FormattedMessage id="supplementalItemsChecklist" />
                                </Typography>
                            </ListItem>
                        }
                    </>
                }
            </List>
        </div>
    );
};
export default ApplicationFormSideBar;
